/* eslint-disable @nx/enforce-module-boundaries */
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer, ɵDomSanitizerImpl } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { from, switchMap, take } from 'rxjs';
import { AppAuthGuard } from './auth.guard';
import { LoginComponent } from './lib/components/login/login.component';
import { httpInterceptor } from './lib/http-interceptor';
import { ConfigService } from './lib/services/config-service/config-service.service';
import { MultipartFormHolderModule } from './lib/services/multipart-holder/multipart-form-holder.module';

// Being used to register custom icons
import { MatIconModule } from '@angular/material/icon';

import { SortablejsModule } from '@maksim_m/ngx-sortablejs';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { LOCAL_STORAGE_ENGINE, NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import jwt_decode from 'jwt-decode';
import player from 'lottie-web/build/player/lottie_svg';
import { NgxJdenticonModule } from 'ngx-jdenticon';

import { waitForFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/flutter.utils';
import { LayoutsModule } from './Modules/layouts/layouts.module';
import { SharedModule } from './Modules/shared/shared.module';
import { ErrorHandlerInterceptor } from './lib/error-interceptor';
import { WebFrontendConfig } from './lib/services/config-service/config.type';
import { InitializationService } from './lib/services/initialization-service/initialization.service';
import { MetadataService } from './lib/services/metadata-service/metadata-service.service';
import { AccountState } from './lib/state/account.state';
import { BeneficiaryState } from './lib/state/beneficiary.state';
import { CardManagementState } from './lib/state/card-management.state';
import { CardState } from './lib/state/card.state';
import { ChartState } from './lib/state/chart.state';
import { FormActionState } from './lib/state/form-submision.state';
import { MonthlySummaryState } from './lib/state/monthlysummary.state';
import { ProgressBarState } from './lib/state/progress-bar.state';
import { RoleState } from './lib/state/role.state';
import { TransactionState } from './lib/state/transaction.state';
import { ProfileState } from './lib/state/user-profile.state';
import { TokenInterceptor } from './lib/token-interceptor';
import { isAuthenticatedRoute } from './lib/utils/auth-checks.utils';

import { CurrencyPipe, DatePipe } from '@angular/common';
import { UiComponentsModule } from '@finxone-platform/ui-components';
import { UI_COMPONENT_ADAPTER_PROVIDERS } from './lib/ui-component-adapters/ui-component-adapters.providers';
import { KeycloakCustomService } from './lib/services/keycloak-wrapper-service/keycloak-custom.service';
import { KeycloakWrapperService } from './lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { CopResponseState } from './lib/state/cop-response.state';
import { ImageActionState } from './lib/state/image-submision.state';
import { PaymentState } from './lib/state/payment.state';
import { SavingsProductsState } from './lib/state/savings-products.state';

import * as Sentry from '@sentry/angular';

import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { PageContextState } from '@app/finxone-web-frontend/app/lib/state/page-context.state';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  KybStatus,
  KycStatus,
  KycStatusRoutingConfig,
  ProfileCompletionStatus,
  ProfileStateModel,
  UserInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { provideLottieOptions } from 'ngx-lottie';
import { FooterNavbarComponent } from './Modules/layouts/footer-navbar/footer-navbar.component';
import { RequestUrlSanitizerInterceptor } from './lib/request-url-santizer-interceptor';
import { WorkflowFormsModule } from './lib/services/workflow-forms/workflow-forms.module';
import { ApiLoadingState } from './lib/state/api-loading.state';
import { AppDataState } from './lib/state/app-data.state';
import { BulkPaymentHistoryState } from './lib/state/bulk-payment-history.state';
import { BulkPaymentListState } from './lib/state/bulk-payment.state';
import { LocaleVariableState } from './lib/state/locale-variable.state';
import { NotificationState } from './lib/state/notifications.state';
import { OrganisationState } from './lib/state/organisation.state';
import { PaymentRequestState } from './lib/state/payment-request.state';
import { PaymentSummaryCounterState } from './lib/state/payment-summary-counters.state';
import { ProjectAlertsState } from './lib/state/project-alerts.state';
import { ProjectSettingsState } from './lib/state/project-settings.state';

import { CookiebotInitService } from '@app/finxone-web-frontend/app/lib/services/cookiebot-service/cookiebot-init.service';
import { isEmpty } from 'lodash';
import { UAParser } from 'ua-parser-js';
import { UpdateCurrentCardDetail } from './lib/actions/card.actions';
import { DedupeInterceptor } from './lib/dedup-interceptor';
import { CardManagementService } from './lib/services/card-management-service/card-management-service';
import { DeviceTypeService } from './lib/services/device-type/device-type.service';
import { GoogleAnalyticsService } from './lib/services/google-analytics/google-analytic.service';
import { HideConditionsService } from './lib/services/hide-conditions-service/hide-conditions.service';
import { DepositAccountInfoState } from './lib/state/deposit-account-info.state';
import { RecurringPaymentsState } from './lib/state/recurring-payments.state';
import { SavingsAccountState } from './lib/state/savings-account.state';
import { TotalWealthState } from './lib/state/total-wealth.state';
import { handlePostLoginRetailUserRouting } from './lib/utils/auth-routing.utils';
import {
  clearLocalStorageExcept,
  getLoginRedirectUri,
  setAlternativeThemeFromQueryParam,
} from './lib/utils/utils';
import { formatNameAsUrl } from './lib/utils/zone-url.utils';

const keycloakService = new KeycloakService();

export const ngxsWebFrontendStateStorageMethods = [
  {
    key: AppDataState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: AccountState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: TotalWealthState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: PaymentState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: CardState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: TransactionState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: BeneficiaryState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: RoleState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: ProgressBarState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: ProfileState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: ChartState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: FormActionState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: ImageActionState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: CopResponseState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: NotificationState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: PageContextState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: OrganisationState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: PaymentRequestState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  { key: BulkPaymentListState, engine: SESSION_STORAGE_ENGINE },
  {
    key: BulkPaymentHistoryState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: ProjectSettingsState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: ApiLoadingState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: ProjectAlertsState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: PaymentSummaryCounterState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: DepositAccountInfoState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: SavingsProductsState,
    engine: SESSION_STORAGE_ENGINE,
  },
  {
    key: SavingsAccountState,
    engine: LOCAL_STORAGE_ENGINE,
  },
  {
    key: RecurringPaymentsState,
    engine: LOCAL_STORAGE_ENGINE,
  },
];
const appInitializeMessage = '[INFO] App initialization:';
const appInitializeErrorMessage = '[ERROR] App initialization:';
const appInitializeWarningMessage = '[WARN] App initialization:';
class Logger {
  info(message: string): void {
    console.info(`%c ${appInitializeMessage} ${message}`, 'background: #92e9fd;');
  }
  warn(message: string): void {
    console.warn(`%c ${appInitializeWarningMessage} ${message}`, 'background: #fff3cd; color: #856404;');
  }
}
const logger = new Logger();

@NgModule({
  declarations: [AppComponent, LoginComponent],
  exports: [RouterModule],
  bootstrap: [AppComponent],
  imports: [
    KeycloakAngularModule,
    BrowserModule,
    NgxsModule.forRoot([
      AppDataState,
      AccountState,
      SavingsProductsState,
      PaymentState,
      TotalWealthState,
      CardState,
      TransactionState,
      MonthlySummaryState,
      BeneficiaryState,
      CopResponseState,
      RoleState,
      CardManagementState,
      ProfileState,
      ProgressBarState,
      ChartState,
      FormActionState,
      ImageActionState,
      NotificationState,
      PageContextState,
      OrganisationState,
      PaymentRequestState,
      BulkPaymentListState,
      BulkPaymentHistoryState,
      ProjectSettingsState,
      ApiLoadingState,
      ProjectAlertsState,
      LocaleVariableState,
      PaymentSummaryCounterState,
      DepositAccountInfoState,
      SavingsAccountState,
      RecurringPaymentsState,
    ]),
    NgxsStoragePluginModule.forRoot({
      keys: ngxsWebFrontendStateStorageMethods,
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    UiComponentsModule,
    MultipartFormHolderModule.forRoot(),
    WorkflowFormsModule.forRoot(),
    FormsModule,
    ReactiveFormsModule.withConfig({
      warnOnNgModelWithFormControl: 'never',
    }),
    BrowserAnimationsModule,
    FooterNavbarComponent,
    LayoutsModule,
    MatIconModule,
    SortablejsModule.forRoot({ animation: 150 }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    // NgxsLoggerPluginModule.forRoot(),
    NgxJdenticonModule,
    SharedModule,
  ],
  providers: [
    ...UI_COMPONENT_ADAPTER_PROVIDERS,
    DatePipe,
    CurrencyPipe,
    CountriesCurrenciesService,
    IconCacheService,
    provideLottieOptions({
      player: () => player,
    }),
    {
      // useExisting (not useClass): DedupeInterceptor is providedIn: 'root' so it can also be
      // injected directly (e.g. by CtaButtonWidgetComponent's executor) - useClass here would
      // construct a second, disconnected instance instead of reusing that singleton.
      provide: HTTP_INTERCEPTORS,
      useExisting: DedupeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: httpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestUrlSanitizerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    AppAuthGuard,
    {
      provide: KeycloakService,
      useValue: keycloakService,
    },
    {
      provide: DomSanitizer,
      useValue: new ɵDomSanitizerImpl(document),
    },
    {
      provide: KeycloakWrapperService,
      deps: [KeycloakService, HttpClient, Store],
    },
    {
      provide: KeycloakCustomService,
      deps: [KeycloakWrapperService],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCookiebot,
      deps: [ConfigService, CookiebotInitService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialize,
      multi: true,
      deps: [
        KeycloakWrapperService,
        ConfigService,
        Router,
        MetadataService,
        CardManagementService,
        Store,
        InitializationService,
        GoogleAnalyticsService,
        Sentry.TraceService,
      ],
    },
    provideHttpClient(withInterceptorsFromDi()),
    HideConditionsService,
  ],
})
export class AppModule {}

/**
 * APP_INITIALIZER factory to initialize Cookiebot after loading the app configuration.
 *
 * Returns a function that the Angular APP_INITIALIZER will invoke. That function
 * waits for the app configuration, then calls CookiebotInitService.initCookiebot(...)
 * with the cookiebotId from the config.
 */
export function initializeCookiebot(
  configService: ConfigService,
  cookiebotService: CookiebotInitService,
): () => import('rxjs').Observable<void> {
  return () =>
    configService.getConfig().pipe(
      take(1),
      switchMap((config) => cookiebotService.initCookiebot(config.cookiebotId || '')),
    );
}

export function initialize(
  keycloak: KeycloakWrapperService,
  configService: ConfigService,
  router: ActivatedRoute,
  metaService: MetadataService,
  cardService: CardManagementService,
  store: Store,
  initializationService: InitializationService,
) {
  return () => {
    //clearing role from sessionStorage on app initialise, this is to combat role being remembered sometimes possibly causing the white screen
    sessionStorage.removeItem('role');
    return configService.getConfig().pipe(
      take(1),
      switchMap((config: WebFrontendConfig) => {
        configChecker(config);

        Sentry.addBreadcrumb({
          category: 'theme',
          message: 'Setting alternative theme from query param',
          level: 'info',
        });
        setAlternativeThemeFromQueryParam();

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Initing keycloak js library',
          level: 'info',
        });

        performance.mark('boot:config-json-loaded');
        return from(
          keycloak.init(keycloak.getBaseKeycloakConfig(config)).then(async (auth) => {
            performance.mark('boot:keycloak-inited');
            logger.info('Token Added');

            try {
              const token = sessionStorage.getItem('token');

              if (auth) {
                logger.info('Auth mode selected: AUTHED');
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: 'Initing keycloak js - user inited and proceeding',
                  level: 'info',
                });
                // Happy path user is authed and initialise app as normal and store the tokens
                // Should get triggered after redirect back from keycloak
                await initAuthedApplication(keycloak, initializationService);
                return;
              }

              if (sessionStorage.getItem('refreshToken') && token) {
                logger.info('Auth mode selected: FLUTTER');
                logger.warn('User not authenticated but refresh token present, checking token validity');
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: 'Initing keycloak js - user not authed but refresh token present',
                  level: 'info',
                });

                if (!(await checkCurrentTokenExpirationAndRenew(token, keycloak))) {
                  logger.info(`Expired or invalid token triggering re-login`);
                  Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Initing keycloak js - exired refresh sending to login',
                    level: 'info',
                  });
                  await tryLoginWithFlutterUsername(keycloak, window.location.origin + '/post-auth');
                }
                configService.setRoleInfo(token);
                logger.info(`roleInfo set= ${configService.roleInfo.role}`);
                return;
              }

              if (isAuthenticatedRoute(window.location.pathname) && window.location.pathname !== '/') {
                logger.info('Auth mode selected: LOGIN REDIRECT');
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: 'Initing keycloak js - not authed not on authed path sending to login ',
                  level: 'info',
                });
                logger.info(`No valid token found and authenticated route, triggering login`);
                await tryLoginWithFlutterUsername(keycloak, getLoginRedirectUri());
                return;
              }

              logger.info('Auth mode selected: UNAUTHED');
              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Initing keycloak js - not authed but no auth needed just setting device',
                level: 'info',
              });
              configService.setDevice();
              logger.info(
                `Device set
                    ${configService.getDeviceData().deviceType}`,
              );
            } catch (error) {
              await recoverFromFailedAuthInit(keycloak, error);
            }
          })
            // init() itself rejects when the seeded refresh token is dead (e.g. the app
            // resumed after the SSO session idled out). Uncaught, bootstrap fails and the
            // static #loader spins forever, so recover the same way.
            .catch((error) => recoverFromFailedAuthInit(keycloak, error)),
        );
      }),
    );
  };
}

export async function recoverFromFailedAuthInit(keycloak: KeycloakWrapperService, error: unknown) {
  console.error(appInitializeErrorMessage, 'Error during authentication initialization', error);

  // Clear potentially invalid tokens
  sessionStorage.clear();
  localStorage.removeItem('kc-callback-state');
  localStorage.removeItem('kc-code-verifier');

  Sentry.captureException(error);
  await tryLoginWithFlutterUsername(keycloak, getLoginRedirectUri());
}

async function tryLoginWithFlutterUsername(keycloak: KeycloakWrapperService, redirectUri: string) {
  const flutterWebView = await waitForFlutterWebView();
  if (flutterWebView) {
    try {
      const username = await flutterWebView.callHandler('getRememberMeUsername', []);
      if (username) {
        keycloak.login({
          redirectUri,
          loginHint: username,
        });
        return;
      }
    } catch {
      // ignore and fallback to normal login
    }
  }
  keycloak.login({
    redirectUri,
  });
}

export async function initAuthedApplication(
  keycloak: KeycloakWrapperService,
  initializationService: InitializationService,
) {
  const token = await keycloak.getToken();
  performance.mark('boot:token-ready');
  logger.info(`initAuthedApplication: clearLocalStorageExcept`);
  clearLocalStorageExcept([
    'account',
    'transactions',
    'beneficiary',
    'notifications',
    'lastLoggedInId',
    'APP_PREFERENCES',
    'firstTime',
    'lastSelectedOrg',
  ]);
  logger.info(`Valid token found, init application`);
  initMobileAppUserIfApplicable(token);
  sessionStorage.setItem('token', token);
  const refreshToken = keycloak.getKeycloakInstance().refreshToken as string;
  sessionStorage.setItem('refreshToken', refreshToken);
  initializationService.initializeApp(token);
}

function initMobileAppUserIfApplicable(token: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flutterWebView = (window as any).flutter_inappwebview;
  if (flutterWebView) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jwt: any = jwt_decode(token);
    if (jwt.email) {
      flutterWebView
        .callHandler('getUsername', [])
        .then((username: null | string) => {
          if (username == null) {
            flutterWebView.callHandler('setUsername', [jwt.email]).then(() => {
              console.log('Set mobile app user');
            });
          }
        })
        .catch(() => {
          console.error('Failed to fetch username from mobile app');
        });
      flutterWebView.callHandler('setRememberMe', [true]).catch(() => {
        console.error('Failed to set remember me for mobile app');
      });
    }
  }
}

/*
  Used to check if token is expired and renew if so
  Returns false if no expiry set so a login should be triggered
*/
export async function checkCurrentTokenExpirationAndRenew(token: string, keycloak: KeycloakWrapperService) {
  if (token === undefined || token === 'undefined') {
    logger.info('Token is undefined in check current token and renew');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    keycloak.login({
      redirectUri: getLoginRedirectUri(),
    });
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jwt: any = jwt_decode(token);
    logger.info(`/app.module jwt decoded for: ${jwt.sub}`);

    if (jwt.exp) {
      // Check token expiry if expired renew?
      const expiryDate = new Date(jwt.exp * 1000);
      if (expiryDate < new Date()) {
        logger.warn('Token is expired, renewing');
        const token = await keycloak.getToken();
        if (token) sessionStorage.setItem('token', token);
        const refreshToken = keycloak.getKeycloakInstance().refreshToken as string;
        if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
      }

      return true;
    } else {
      return false;
    }
  } catch (err) {
    logger.info('/app.module checkCurrentTokenExpirationAndRenew error block, navigating to /post-auth');
    // if token decode failed then just send to login
    Sentry.captureException(err);
    keycloak.login({
      redirectUri: getLoginRedirectUri(),
    });
    return false;
  }
}

export function verifyRoute(
  router: Router,
  profile: UserInfoType & ProfileStateModel,
  isLandingRoutingEnable = true,
  cardService: CardManagementService,
  store: Store,
  userId?: string,
  redirectForApplePayEnabled = true,
  returnUrl?: string,
  deviceTypeService?: DeviceTypeService,
  kycStatusRoutingConfig?: KycStatusRoutingConfig | null,
  route?: ActivatedRoute,
): boolean {
  const {
    organisationKybStatus,
    activeOrganisationKybStatus,
    role,
    activeRole,
    profileCompletionStatus,
    kycVerificationStatus,
  } = profile || {};

  const isReviewPending =
    organisationKybStatus === KybStatus.REVIEW_PENDING ||
    activeOrganisationKybStatus === KybStatus.REVIEW_PENDING;

  const isPending =
    organisationKybStatus === KybStatus.PENDING ||
    organisationKybStatus === KybStatus.STARTED ||
    activeOrganisationKybStatus === KybStatus.PENDING ||
    activeOrganisationKybStatus === KybStatus.KYB_PENDING ||
    activeOrganisationKybStatus === KybStatus.FAILED ||
    activeOrganisationKybStatus === KybStatus.STARTED ||
    activeOrganisationKybStatus === KybStatus.REVIEW_PENDING ||
    activeOrganisationKybStatus === KybStatus.IN_PROGRESS ||
    profileCompletionStatus === ProfileCompletionStatus.PENDING ||
    kycVerificationStatus === KycStatus.PENDING ||
    kycVerificationStatus === KycStatus.KYC_PENDING ||
    kycVerificationStatus === KycStatus.FAILED ||
    kycVerificationStatus === KycStatus.IN_PROGRESS ||
    kycVerificationStatus === KycStatus.STARTED ||
    kycVerificationStatus === KycStatus.NOT_STARTED;

  const isOrgNotAvailable = !!(profile.orgList?.length === 0);

  const isOwner = activeRole === 'owner' || role === 'owner';

  const redirectToOrgOnboardingFlow =
    store?.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
      ?.redirectToOrgOnboardingFlow ?? false;

  if (redirectToOrgOnboardingFlow) {
    router.navigateByUrl('/zones/organisation-onboarding/onboarding/organisation-anonymous/default');
    return true;
  }

  if (kycStatusRoutingConfig && kycStatusRoutingConfig?.retail && !isEmpty(kycStatusRoutingConfig?.retail)) {
    const autoLogin = route?.snapshot.queryParamMap?.get('autoLogin') === 'true';
    const isSafeReturnUrl = !!returnUrl && returnUrl.startsWith('/');
    // deep links win over the KYC status routing config for verified users;
    // pending/failed/cancelled users are still routed by the KYC config first
    if (isSafeReturnUrl && (autoLogin || kycVerificationStatus === KycStatus.SUCCESS)) {
      router.navigateByUrl(returnUrl);
      return true;
    }

    const handled = handlePostLoginRetailUserRouting(
      kycStatusRoutingConfig,
      router,
      kycVerificationStatus,
      deviceTypeService,
    );
    if (handled) return true;
  }

  if ((isReviewPending && isOwner) || isPending || (isOrgNotAvailable && isOwner)) {
    router.navigateByUrl('zones/verification/status-screen');
    return true;
  } else {
    const isSafeReturnUrl = !!returnUrl && returnUrl.startsWith('/');
    if (isSafeReturnUrl) {
      router.navigateByUrl(returnUrl);
      return false;
    }
    if (isLandingRoutingEnable) {
      handleUserRouting(
        userId,
        router,
        isLandingRoutingEnable,
        store,
        cardService,
        redirectForApplePayEnabled,
      );
    }
    return false;
  }
}

/**
 * Determines the appropriate route based on user ID, platform, and whether the user has seen the Apple Pay splash screen.
 *
 * @param userId - The user's unique identifier (undefined if not logged in)
 * @param router - The router object used for navigation
 * @param isLandingRoutingEnable - Flag indicating if landing page routing is enabled
 * @returns The URL that was navigated to
 */
function handleUserRouting(
  userId: string | undefined,
  router: any, // Replace with your router type
  isLandingRoutingEnable: boolean,
  store: Store,
  cardsService: CardManagementService,
  redirectForApplePayEnabled: boolean,
): string {
  // Default route
  const defaultRoute = 'zones/landing/home';

  // If landing routing is disabled or user is not logged in, go to default route
  if (!isLandingRoutingEnable || !userId) {
    router.navigateByUrl(defaultRoute);
    return defaultRoute;
  }

  // Check if we're in a Flutter WebView
  const flutterWebView = (window as any).flutter_inappwebview;
  if (!flutterWebView) {
    router.navigateByUrl(defaultRoute);
    return defaultRoute;
  }

  // Check if the device is iOS
  const os = UAParser().os;
  if (os.name !== 'iOS') {
    router.navigateByUrl(defaultRoute);
    return defaultRoute;
  }

  // Check if the user has already seen the Apple Pay splash screen
  const seenApplePaySplashUsers: string[] | undefined = (window as any).seenApplePaySplashUsers;

  if (seenApplePaySplashUsers === undefined) {
    // if not set then navigate to default route
    router.navigateByUrl(defaultRoute);
    return defaultRoute;
  }

  // If user hasn't seen the splash screen, show it
  // can toggle off this redirect by setting feature flag in config.json to false
  if (!seenApplePaySplashUsers.includes(userId) && redirectForApplePayEnabled) {
    cardsService.getCards().subscribe(async (cards) => {
      for (const card of cards.result) {
        const currentCardId = card.id;
        const currentCardMaskedPan = card.maskedPan;

        const cardsOnDevice: { tokenList: { primaryAccountSuffix: string }[] } =
          (await flutterWebView.callHandler('getTokenList', [])) || { tokenList: [] };

        const cardIsNotOnDevice = !cardsOnDevice.tokenList.some(
          (token) => token.primaryAccountSuffix === currentCardMaskedPan.slice(-4),
        );

        if (cardIsNotOnDevice) {
          // set current card to one not on apple pay
          store.dispatch(new UpdateCurrentCardDetail(card, currentCardId));

          const applePaySplashRoute = `zones/${formatNameAsUrl(
            APP_ZONES.CARD_MANAGEMENT_ZONE,
          )}/add-card-to-apple-pay`;
          router.navigateByUrl(applePaySplashRoute);
        }
      }
    });

    return '';
  }

  // Otherwise, go to the default route
  router.navigateByUrl(defaultRoute);
  return defaultRoute;
}

function configChecker(config: WebFrontendConfig) {
  try {
    if (!config.api_endpoints['auth_service']) {
      console.error(appInitializeErrorMessage, "Missing config.api_endpoints['auth_service']");
      throw new Error("Missing config.api_endpoints['auth_service']");
    } else {
      logger.info(`config.api_endpoints['auth_service']= ${config.api_endpoints['auth_service']}`);
    }

    if (!config.auth.realm) {
      console.error(appInitializeErrorMessage, 'Missing config.auth.realm');
      throw new Error('Missing config.auth.realm');
    } else {
      logger.info(`config.auth.realm= ${config.auth.realm}`);
    }

    if (!config.auth.client_id) {
      console.error(appInitializeErrorMessage, 'Missing config.auth.client_id');
      throw new Error('Missing config.auth.client_id');
    } else {
      logger.info(`clientId= ${config.auth.client_id}`);
    }

    // Continue with the app initialization process...
  } catch (error) {
    if (error instanceof Error) {
      console.error(appInitializeErrorMessage, 'App initialization failed:', error.message);
    } else {
      console.error(appInitializeErrorMessage, 'App initialization failed with an unknown error.');
    }
    // Stop the loading process or take other necessary actions
  }
}
